import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Globe, Building2, Bot, Eye, Zap, CheckCircle2 } from 'lucide-react';

/**
 * UrlToCompanyChart — the five-step path from a pasted URL to a reviewed,
 * running AI workforce (blog07). Compact, animated, blog-appropriate.
 * Auto-advances while in view; pauses on hover so a reader can read a step.
 */

const STEPS = [
  {
    title: 'Paste a URL',
    icon: <Globe className="w-5 h-5" />,
    color: 'text-cyan-600 dark:text-cyan-400',
    bg: 'bg-cyan-100 dark:bg-cyan-500/15',
    detail: 'A company website or LinkedIn page is enough.',
  },
  {
    title: 'Read your public presence',
    icon: <Building2 className="w-5 h-5" />,
    color: 'text-teal-600 dark:text-teal-400',
    bg: 'bg-teal-100 dark:bg-teal-500/15',
    detail: 'Brand voice, value props, products, personas — turned into a company profile.',
  },
  {
    title: 'Propose departments & bots',
    icon: <Bot className="w-5 h-5" />,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-100 dark:bg-emerald-500/15',
    detail: 'Bot roles and the processes they would run, per function.',
  },
  {
    title: 'You review',
    icon: <Eye className="w-5 h-5" />,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-100 dark:bg-amber-500/15',
    detail: 'Approve, edit, or reject each proposal before anything deploys.',
  },
  {
    title: 'Bots earn autonomy',
    icon: <Zap className="w-5 h-5" />,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-100 dark:bg-green-500/15',
    detail: 'Each bot is promoted at its own pace as your approvals build confidence.',
  },
];

export const UrlToCompanyChart = ({ height = 340 }: { height?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!inView || paused) return;
    const t = setInterval(() => setActive((p) => (p + 1) % STEPS.length), 2200);
    return () => clearInterval(t);
  }, [inView, paused]);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-4 sm:p-6"
      style={{ minHeight: height }}
    >
      <div className="space-y-3">
        {STEPS.map((s, i) => {
          const isActive = i === active;
          const isDone = i < active;
          return (
            <motion.div
              key={s.title}
              animate={isActive ? { scale: 1.02 } : { scale: 1 }}
              className={`relative rounded-lg border p-3 flex items-start gap-3 transition-colors duration-300 ${
                isActive
                  ? `${s.bg} border-teal-500/50`
                  : isDone
                  ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-500/30'
                  : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700'
              }`}
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                  isDone ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : isActive ? `${s.bg} ${s.color}` : 'bg-slate-200/60 text-slate-400 dark:bg-slate-700/40'
                }`}
              >
                {isDone ? <CheckCircle2 className="w-5 h-5" /> : s.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm font-bold ${isActive ? s.color : 'text-slate-700 dark:text-slate-300'}`}>
                    {i + 1}. {s.title}
                  </span>
                  {isActive && (
                    <span className="text-label-2xs px-2 py-0.5 rounded-full bg-teal-500/15 text-teal-600 dark:text-teal-400 font-medium">
                      now
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{s.detail}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. The path from URL to a reviewed, running workforce — five steps, and a human reviews before anything
        deploys. The loop pauses on hover.
      </p>
    </div>
  );
};
